/**
 * useLiveSessionsListState — navigation + menu actions for the live-sessions list.
 *
 * Shared by "Tous mes lives" AND the parent home (`useParentHomeState`), so the routing below is
 * written once. Owns confirm / message-modal visibility (useReducer) and routes `LiveMenuAction`s:
 * - manage_rdv → three branches on `roomType` (legacy `StudentLiveCardComponent`): group →
 *   legacy `absence-form`; trial → WhatsApp / email; individual & co → contact-tutor dialog
 * - message_tutor → DS SendTutorMessageModal
 * - cancel_* → DS ConfirmDialog → cancelAttendanceFlag (+ error feedback)
 *
 * Les surfaces parent n'ouvrent plus de menu « ⋮ » mais une feuille d'actions
 * (`<LiveSessionActionsSheet>`), dont les gestes passent par `useParentSessionGesture`. Ce hook
 * ne garde donc du geste que ce qui lui appartient : le composeur de message (ouvert par tuteur,
 * pas par séance) et les trois annulations, que le geste lui délègue.
 *
 * `onLiveMenuAction` — et avec lui « Gérer le rdv » et ses trois branches — reste pour un menu
 * legacy non parent (`<LiveSessionMenu>`) : aucun écran du design system parent ne l'appelle plus.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigation } from "@react-navigation/native"
import { Linking } from "react-native"

import { apiIdle, apiRequest } from "../../../../../../data/redux/actions/api"
import { attendanceFlagsService } from "../../../../../../data/services/attendanceFlagsService"
import { type LiveMenuAction, type LiveSessionAction } from "../../../../../../design-system"
import { formatDayMonthYear, formatHour } from "../../../formatters/dateFormatter"
import { GROUP_ROOM_TYPES, tutorShortName } from "../../../formatters/liveSessionFormatter"
import { type UILiveSession, type UITutor } from "../../../types"
import { apiErrorMessage } from "../../../hooks/apiErrorMessage"
import { useGestureEmitter } from "../../../hooks/gestureTracking"
import { SUPPORT_MAILTO_URL, TRIAL_WHATSAPP_URL } from "../../../hooks/useParentSessionGesture"
import { useSendTutorMessageAdapter } from "../../../hooks/useSendTutorMessageAdapter"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

export type CancelKind = "absence" | "late" | "recovery"

/** Legacy `getSessionTypeLabel` — titles the contact-tutor dialog. */
function sessionTypeLabel(roomType: string | undefined): string {
    if (roomType === "support") return "Live de support technique"
    if (roomType === "custom") return "Live personnalisé"
    return "Séance individuelle"
}

/**
 * The "Gérer le rdv" dialog, for the two branches that don't navigate.
 * `confirm` / `cancel` are both real actions on `trial` (WhatsApp / Email), hence `onDismiss`
 * on the DS dialog: a backdrop tap must close, not send a mail (legacy is `dismissable={false}`).
 */
type ManageRdvDialog = {
    kind: "trial" | "contact_tutor"
    session: UILiveSession
    title: string
    message: string
    confirmLabel: string
    cancelLabel: string
}

/** Le geste mesuré d'une annulation — le nom de l'action de la feuille qui l'a ouverte. */
const CANCEL_GESTURE: Record<CancelKind, LiveSessionAction> = {
    absence: "cancel_absence",
    late: "cancel_late",
    recovery: "cancel_recovery",
}

export type CancelConfirm = {
    kind: CancelKind
    session: UILiveSession
    title: string
    message: string
    flagId: string | number
}

type Feedback = {
    title: string
    message: string
    /** Un échec (l'annulation refusée) ; absent : un succès (le message envoyé). */
    error?: boolean
}

/**
 * Le composeur ouvert : un tuteur, pas une séance — « Quel tuteur ? » en désigne un. `onBack` est
 * le retour du parcours qui l'a ouvert, quand il en est la 2ᵉ étape ; seul, il n'en a pas.
 */
type MessageTarget = {
    tutor: UITutor
    onBack?: () => void
}

type State = {
    message: MessageTarget | null
    cancelConfirm: CancelConfirm | null
    manageRdv: ManageRdvDialog | null
    feedback: Feedback | null
}

type Action =
    | { type: "OPEN_MESSAGE"; message: MessageTarget }
    | { type: "CLOSE_MESSAGE" }
    | { type: "OPEN_CANCEL"; confirm: CancelConfirm }
    | { type: "CLOSE_CANCEL" }
    | { type: "OPEN_MANAGE_RDV"; dialog: ManageRdvDialog }
    | { type: "CLOSE_MANAGE_RDV" }
    | { type: "OPEN_FEEDBACK"; feedback: Feedback }
    | { type: "CLOSE_FEEDBACK" }

const initialState: State = {
    message: null,
    cancelConfirm: null,
    manageRdv: null,
    feedback: null,
}

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "OPEN_MESSAGE":
            // The contact-tutor dialog hands over to the modal — never stack the two.
            return { ...state, message: action.message, manageRdv: null }
        case "CLOSE_MESSAGE":
            return { ...state, message: null }
        case "OPEN_CANCEL":
            return { ...state, cancelConfirm: action.confirm }
        case "CLOSE_CANCEL":
            return { ...state, cancelConfirm: null }
        case "OPEN_MANAGE_RDV":
            return { ...state, manageRdv: action.dialog }
        case "CLOSE_MANAGE_RDV":
            return { ...state, manageRdv: null }
        case "OPEN_FEEDBACK":
            return { ...state, feedback: action.feedback }
        case "CLOSE_FEEDBACK":
            return { ...state, feedback: null }
        default:
            return state
    }
}

/**
 * La question d'une annulation, avec la date et l'heure de ce qu'on annule. L'heure s'écrit
 * « 18h00 » (`formatHour`), comme partout dans l'espace parent — pas « 18:00 ».
 */
export function buildCancelConfirm(session: UILiveSession, kind: CancelKind): CancelConfirm | null {
    if (kind === "late") {
        if (session.lateFlagId == null) return null
        return {
            kind,
            session,
            flagId: session.lateFlagId,
            title: "Annuler le retard ?",
            message: `Êtes-vous sûr de vouloir annuler le retard du ${formatDayMonthYear(session.date)} à ${formatHour(session.date)} ?`,
        }
    }

    if (kind === "recovery") {
        if (session.recoveryFlagId == null) return null
        const dateToShow = session.recoverySourceDate ?? session.date
        return {
            kind,
            session,
            flagId: session.recoveryFlagId,
            title: "Annuler l'absence ?",
            message: `Êtes-vous sûr de vouloir annuler l'absence du ${formatDayMonthYear(dateToShow)} à ${formatHour(dateToShow)} ?\n\nLe rattrapage prévu le ${formatDayMonthYear(session.date)} à ${formatHour(session.date)} sera également annulé.`,
        }
    }

    if (session.absenceFlagId == null) return null
    let recoverySuffix = ""
    if (session.recoveryOccurrenceDate) {
        recoverySuffix = `\n\nLe rattrapage prévu le ${formatDayMonthYear(session.recoveryOccurrenceDate)} à ${formatHour(session.recoveryOccurrenceDate)} sera également annulé.`
    } else if (session.recoveryOccurrenceId != null) {
        recoverySuffix = "\n\nLe rattrapage planifié sera également annulé."
    }
    return {
        kind,
        session,
        flagId: session.absenceFlagId,
        title: "Annuler l'absence ?",
        message: `Êtes-vous sûr de vouloir annuler l'absence du ${formatDayMonthYear(session.date)} à ${formatHour(session.date)} ?${recoverySuffix}`,
    }
}

export type UseLiveSessionsListStateArgs = {
    studentEmail?: string | null
}

export function useLiveSessionsListState(args: UseLiveSessionsListStateArgs = {}) {
    const { studentEmail = null } = args
    const dispatch = useDispatch()
    const emitGesture = useGestureEmitter()
    const navigation = useNavigation<{
        canGoBack: () => boolean
        goBack: () => void
        replace: (name: string) => void
        push: (name: string, params?: Record<string, unknown>) => void
        navigate: (name: string, params?: Record<string, unknown>) => void
    }>()

    const student = useSelector(selectDisplayedStudent)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isParentMode = useSelector((state: any) => !!state?.data?.family?.isParentMode)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cameraOn = useSelector((state: any) => !!state?.activity?.cameraOn)
    const cancelReq = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) =>
            state?.api?.cancelAttendanceFlag as
                | { request?: boolean; success?: boolean; error?: unknown; routeParams?: { id?: string | number } }
                | undefined
    )

    const studentId = student?.id as string | number | undefined
    const [state, dispatchUi] = useReducer(reducer, initialState)

    const awaitingCancelRef = useRef(false)
    const handledCancelErrorRef = useRef(false)
    /** L'annulation en vol : son drapeau, et le geste mesuré qu'elle conclut. */
    const pendingCancelRef = useRef<{ flagId: string | number; gesture: LiveSessionAction } | null>(null)

    const close = useCallback(() => {
        if (navigation.canGoBack()) navigation.goBack()
        else navigation.replace("menu")
    }, [navigation])

    const openMessageSuccess = useCallback(() => {
        dispatchUi({
            type: "OPEN_FEEDBACK",
            feedback: {
                title: "Message envoyé ✅",
                message: "Votre message a bien été transmis.",
            },
        })
    }, [])

    const tutor = state.message?.tutor ?? null
    const messageBack = state.message?.onBack
    // « Camille R. », comme partout où l'espace nomme un tuteur — jamais le nom de famille entier.
    const tutorDisplayName = (tutor != null ? tutorShortName(tutor) : "") || "votre tuteur"

    const messageModal = useSendTutorMessageAdapter({
        studentId,
        tutorId: tutor?.id,
        tutorDisplayName,
        visible: state.message != null,
        onClose: () => dispatchUi({ type: "CLOSE_MESSAGE" }),
        // Le composeur se referme dans le même rendu que l'étape d'avant se rouvre : une seule feuille.
        onBack:
            messageBack != null
                ? () => {
                      dispatchUi({ type: "CLOSE_MESSAGE" })
                      messageBack()
                  }
                : undefined,
        onSuccess: openMessageSuccess,
        hidden: cameraOn,
        showParentHint: isParentMode || !!studentEmail,
    })

    const asParent = !!studentEmail || isParentMode

    const onLiveMenuAction = useCallback(
        (session: UILiveSession, action: LiveMenuAction) => {
            if (action === "message_tutor") {
                dispatchUi({ type: "OPEN_MESSAGE", message: { tutor: session.tutor } })
                return
            }

            if (action === "manage_rdv") {
                // Three branches on `live_room.type` — literal port of legacy
                // `StudentLiveCardComponent`'s "Gérer le rdv" handler.
                if (GROUP_ROOM_TYPES.includes(session.roomType ?? "")) {
                    if (studentId == null || session.liveSessionId == null) return
                    navigation.push("absence-form", {
                        id: studentId,
                        liveSessionId: session.liveSessionId,
                        liveRoomId: session.liveRoomId,
                        withStudent: asParent,
                        occurrenceId: session.id,
                        occurrenceDate: session.date,
                    })
                    return
                }

                if (session.roomType === "trial") {
                    dispatchUi({
                        type: "OPEN_MANAGE_RDV",
                        dialog: {
                            kind: "trial",
                            session,
                            title: "Séance d'essai",
                            message:
                                "Pour toute question concernant votre séance d'essai (report, annulation), contactez notre équipe.",
                            confirmLabel: "WhatsApp",
                            cancelLabel: "Email",
                        },
                    })
                    return
                }

                dispatchUi({
                    type: "OPEN_MANAGE_RDV",
                    dialog: {
                        kind: "contact_tutor",
                        session,
                        title: sessionTypeLabel(session.roomType),
                        message:
                            "Pour toute question de planification (absence, retard, déplacement de séance), contactez directement votre tuteur. Il pourra vous répondre et adapter le rdv si besoin.",
                        confirmLabel: asParent ? "Contacter le tuteur" : "Contacter mon tuteur",
                        cancelLabel: "Retour",
                    },
                })
                return
            }

            const kind: CancelKind | null =
                action === "cancel_absence"
                    ? "absence"
                    : action === "cancel_late"
                      ? "late"
                      : action === "cancel_recovery"
                        ? "recovery"
                        : null
            if (kind == null) return
            const confirm = buildCancelConfirm(session, kind)
            if (confirm == null) return
            emitGesture("opened", { gesture: CANCEL_GESTURE[kind] })
            dispatchUi({ type: "OPEN_CANCEL", confirm })
        },
        [navigation, studentId, asParent, emitGesture]
    )

    const confirmManageRdv = useCallback(() => {
        const dialog = state.manageRdv
        if (dialog == null) return
        if (dialog.kind === "trial") {
            dispatchUi({ type: "CLOSE_MANAGE_RDV" })
            void Linking.openURL(TRIAL_WHATSAPP_URL)
            return
        }
        if (asParent) {
            dispatchUi({ type: "OPEN_MESSAGE", message: { tutor: dialog.session.tutor } })
            return
        }
        dispatchUi({ type: "CLOSE_MANAGE_RDV" })
        navigation.navigate("messenger", { tutorId: dialog.session.tutor?.id })
    }, [state.manageRdv, asParent, navigation])

    const cancelManageRdv = useCallback(() => {
        const kind = state.manageRdv?.kind
        dispatchUi({ type: "CLOSE_MANAGE_RDV" })
        // "Retour" on the contact-tutor branch only closes (legacy `onPressBack: () => {}`).
        if (kind === "trial") void Linking.openURL(SUPPORT_MAILTO_URL)
    }, [state.manageRdv?.kind])

    const confirmCancel = useCallback(() => {
        const confirm = state.cancelConfirm
        dispatchUi({ type: "CLOSE_CANCEL" })
        if (confirm?.flagId == null) return
        awaitingCancelRef.current = true
        handledCancelErrorRef.current = false
        pendingCancelRef.current = { flagId: confirm.flagId, gesture: CANCEL_GESTURE[confirm.kind] }
        dispatch(apiRequest(attendanceFlagsService.cancelAttendanceFlag, { id: confirm.flagId }) as never)
        emitGesture("submitted", { gesture: CANCEL_GESTURE[confirm.kind] })
    }, [dispatch, state.cancelConfirm, emitGesture])

    // Ports replacement for useApiStatus: surface cancel failures via ConfirmDialog feedback.
    useEffect(() => {
        if (!awaitingCancelRef.current || !cancelReq?.error || handledCancelErrorRef.current) return
        const canceledId = cancelReq.routeParams?.id
        const pending = pendingCancelRef.current
        if (pending != null && canceledId != null && String(canceledId) !== String(pending.flagId)) {
            return
        }
        handledCancelErrorRef.current = true
        awaitingCancelRef.current = false
        pendingCancelRef.current = null
        if (pending != null) emitGesture("failed", { gesture: pending.gesture }, cancelReq.error)
        dispatchUi({
            type: "OPEN_FEEDBACK",
            feedback: {
                title: "Erreur",
                message: apiErrorMessage(cancelReq.error, "Impossible d'annuler. Veuillez réessayer."),
                error: true,
            },
        })
        dispatch(apiIdle(attendanceFlagsService.cancelAttendanceFlag) as never)
    }, [dispatch, cancelReq?.error, cancelReq?.routeParams?.id, emitGesture])

    useEffect(() => {
        if (!awaitingCancelRef.current || !cancelReq?.success) return
        awaitingCancelRef.current = false
        const pending = pendingCancelRef.current
        pendingCancelRef.current = null
        handledCancelErrorRef.current = false
        if (pending != null) emitGesture("succeeded", { gesture: pending.gesture })
        dispatch(apiIdle(attendanceFlagsService.cancelAttendanceFlag) as never)
    }, [dispatch, cancelReq?.success, emitGesture])

    useEffect(
        () => () => {
            dispatch(apiIdle(attendanceFlagsService.cancelAttendanceFlag) as never)
        },
        [dispatch]
    )

    /**
     * « Quel tuteur ? » désigne le destinataire, le composeur reste ici — il n'y en a qu'un.
     * `onBack` : le retour vers l'étape qui l'a ouvert, quand il y en a une.
     */
    const openMessageForTutor = useCallback(
        (tutor: UITutor, onBack?: () => void) => dispatchUi({ type: "OPEN_MESSAGE", message: { tutor, onBack } }),
        []
    )

    const actions = useMemo(
        () => ({
            close,
            onLiveMenuAction,
            openMessageForTutor,
            closeCancel: () => dispatchUi({ type: "CLOSE_CANCEL" }),
            confirmCancel,
            confirmManageRdv,
            cancelManageRdv,
            dismissManageRdv: () => dispatchUi({ type: "CLOSE_MANAGE_RDV" }),
            closeFeedback: () => dispatchUi({ type: "CLOSE_FEEDBACK" }),
        }),
        [close, onLiveMenuAction, openMessageForTutor, confirmCancel, confirmManageRdv, cancelManageRdv]
    )

    return {
        actions,
        messageModal,
        cancelConfirm: state.cancelConfirm,
        manageRdv: state.manageRdv,
        feedback: state.feedback,
    }
}
